/**
 * Step 1: Basic Info + Script Upload
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Tabs,
  Tag,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useWizard } from './WizardContext';
import { adminAPI } from '../../services/api';
import type { ModelCandidatePackage } from '../../types';
import { formatErrorMessage } from '../../utils/errorHandler';

interface Step1Props {
  data: any;
  onChange: (data: any) => void;
  formRef?: React.MutableRefObject<any>;
}

const inferScriptBaseFromUploads = (...fileLists: any[]) => {
  const files = fileLists.flatMap((fileList) => (Array.isArray(fileList) ? fileList : []));
  const bases = files
    .map((item) => {
      const fileName = String(item?.name || item?.originFileObj?.name || '').trim();
      if (!fileName) return '';
      return fileName.replace(/\.[^.]+$/, '');
    })
    .filter(Boolean);
  return bases[0] || '';
};

const getScriptStatusMeta = (status?: string) => {
  switch (status) {
    case 'complete':
      return { type: 'success' as const, label: '完整' };
    case 'partial':
      return { type: 'warning' as const, label: '部分完成' };
    default:
      return { type: 'info' as const, label: '待上传' };
  }
};

const normalizeUploadEvent = (event: any) => {
  if (Array.isArray(event)) return event;
  return event?.fileList || [];
};

interface ScriptFilePickerProps {
  value?: any[];
  onChange?: (fileList: any[]) => void;
  accept: string;
  buttonText: string;
  disabled?: boolean;
}

const ScriptFilePicker: React.FC<ScriptFilePickerProps> = ({
  value = [],
  onChange,
  accept,
  buttonText,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectedFile = Array.isArray(value) ? value[0] : undefined;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    onChange?.([{
      uid: `${Date.now()}-${file.name}`,
      name: file.name,
      status: 'done',
      originFileObj: file,
    }]);
    event.target.value = '';
  };

  const handleClear = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onChange?.([]);
  };

  return (
    <Space size={[8, 8]} wrap>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Button
        icon={<UploadOutlined />}
        aria-label={buttonText}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {buttonText}
      </Button>
      {selectedFile ? (
        <Tag closable onClose={handleClear}>
          {selectedFile.name || selectedFile.originFileObj?.name}
        </Tag>
      ) : null}
    </Space>
  );
};

const fillEmptyField = (
  form: ReturnType<typeof Form.useForm>[0],
  currentValues: any,
  fieldName: 'model_id' | 'script_file_base',
  nextValue: string,
) => {
  const normalizedValue = String(nextValue || '').trim();
  if (!normalizedValue || String(currentValues?.[fieldName] || '').trim()) {
    return currentValues;
  }

  form.setFieldValue(fieldName, normalizedValue);
  return {
    ...currentValues,
    [fieldName]: normalizedValue,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const manifestModelId = (candidate: ModelCandidatePackage) => (
  typeof candidate.manifest.model_id === 'string'
    ? candidate.manifest.model_id
    : undefined
);

const Step1BasicInfo: React.FC<Step1Props> = ({ data, onChange, formRef }) => {
  const [form] = Form.useForm();
  const { modelId, isDraft, loadWizardData, setCurrentStep } = useWizard();
  const candidateInputRef = useRef<HTMLInputElement | null>(null);
  const scriptInfo = data?.script_info;
  const scriptStatus = getScriptStatusMeta(scriptInfo?.registration_status);
  const candidatePackage = isRecord(data?.candidate_package)
    ? data.candidate_package as unknown as ModelCandidatePackage
    : undefined;
  const qualificationStatus = String(
    data?.candidate_qualification_status
    || candidatePackage?.status
    || 'not_started',
  );
  const reviewStatus = String(data?.review_status || 'pending');
  const packageManaged = Boolean(data?.candidate_package_id || candidatePackage);
  const legacyLocked = !isDraft || packageManaged
    || ['platform_qualified', 'clinical_validated'].includes(qualificationStatus);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [savingEditor, setSavingEditor] = useState(false);
  const [candidateUploading, setCandidateUploading] = useState(false);
  const [importMode, setImportMode] = useState('candidate');
  const [scriptEditor, setScriptEditor] = useState({
    file_base: '',
    cpp_filename: '',
    r_filename: '',
    cpp_content: '',
    r_content: '',
  });

  const resolvedModelId = String(modelId || data?.model_id || '').trim();
  const canUploadCandidate = Boolean(resolvedModelId) && (isDraft || !packageManaged);
  const packageGuidance = !isDraft && packageManaged
    ? { type: 'success' as const, title: '模型包已纳入版本管理', detail: '在模型模块继续资格、审核和发布。' }
    : !isDraft
      ? { type: 'warning' as const, title: '尚未关联模型包', detail: '上传后可开始技术资格检查。' }
      : !modelId
        ? { type: 'info' as const, title: '先保存基本信息', detail: '生成模型 ID 后即可上传模型包。' }
        : candidatePackage
          ? { type: 'success' as const, title: '模型包已关联', detail: '可继续完成资料登记。' }
          : { type: 'info' as const, title: '模型包可后补', detail: '当前可先登记为停用模型，发布前需补齐。' };

  const editorTabs = useMemo(
    () => [
      {
        key: 'cpp',
        label: scriptEditor.cpp_filename || 'CPP',
        children: (
          <Input.TextArea
            value={scriptEditor.cpp_content}
            onChange={(event) => setScriptEditor((prev) => ({ ...prev, cpp_content: event.target.value }))}
            autoSize={{ minRows: 14, maxRows: 26 }}
            spellCheck={false}
            readOnly={legacyLocked}
            style={{ fontFamily: 'Consolas, Monaco, monospace' }}
            placeholder="暂无 .cpp 脚本内容"
          />
        ),
      },
      {
        key: 'r',
        label: scriptEditor.r_filename || 'R',
        children: (
          <Input.TextArea
            value={scriptEditor.r_content}
            onChange={(event) => setScriptEditor((prev) => ({ ...prev, r_content: event.target.value }))}
            autoSize={{ minRows: 14, maxRows: 26 }}
            spellCheck={false}
            readOnly={legacyLocked}
            style={{ fontFamily: 'Consolas, Monaco, monospace' }}
            placeholder="暂无 .R 脚本内容"
          />
        ),
      },
    ],
    [legacyLocked, scriptEditor.cpp_content, scriptEditor.cpp_filename, scriptEditor.r_content, scriptEditor.r_filename],
  );

  const openScriptEditor = async () => {
    if (!resolvedModelId) {
      message.warning('请先保存模型基本信息后再查看脚本');
      return;
    }

    setEditorOpen(true);
    setEditorLoading(true);
    try {
      const response = await adminAPI.getModelScriptContent(resolvedModelId);
      setScriptEditor({
        file_base: response.file_base || '',
        cpp_filename: response.cpp_filename || '',
        r_filename: response.r_filename || '',
        cpp_content: response.cpp_content || '',
        r_content: response.r_content || '',
      });
    } catch (error) {
      message.error(formatErrorMessage(error, '加载脚本内容失败'));
      setEditorOpen(false);
    } finally {
      setEditorLoading(false);
    }
  };

  const handleSaveScriptEditor = async () => {
    if (legacyLocked) {
      message.warning('模型包管理的模型脚本不可直接修改');
      return;
    }
    if (!resolvedModelId) {
      message.warning('模型尚未创建，暂时无法保存脚本内容');
      return;
    }

    setSavingEditor(true);
    try {
      const response = await adminAPI.updateModelScriptContent(resolvedModelId, {
        cpp_content: scriptEditor.cpp_content,
        r_content: scriptEditor.r_content,
      });
      message.success(response.message || '脚本内容已保存');
      onChange({
        ...data,
        script_info: response.script_info || data?.script_info,
      });
      setEditorOpen(false);
    } catch (error) {
      message.error(formatErrorMessage(error, '保存脚本内容失败'));
    } finally {
      setSavingEditor(false);
    }
  };

  const handleCandidateFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!resolvedModelId || (!isDraft && packageManaged)) {
      message.warning(
        !isDraft && packageManaged
          ? '非草稿模型仅可查看模型包状态'
          : '请先保存基本信息，创建草稿 model_id 后再上传模型包',
      );
      return;
    }

    setCandidateUploading(true);
    try {
      const response = await adminAPI.uploadModelCandidatePackage(file, resolvedModelId);
      const responseManifestModelId = manifestModelId(response);
      if (response.model_id !== resolvedModelId || responseManifestModelId !== resolvedModelId) {
        message.error(`模型包中的模型 ID 与当前模型不一致：期望 ${resolvedModelId}`);
        return;
      }
      onChange({
        ...data,
        candidate_package: response,
        candidate_package_id: response.id,
        candidate_qualification_status: response.status,
      });
      if (isRecord(response.manifest.registration)) {
        await loadWizardData(resolvedModelId);
        setCurrentStep(0);
      }
      message.success(response.reused ? '已关联现有模型包' : '模型包上传并关联成功');
    } catch (error) {
      message.error(formatErrorMessage(error, '模型包上传失败'));
    } finally {
      setCandidateUploading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      model_id: data?.model_id || '',
      display_name: data?.display_name || data?.model_id || '',
      first_author: data?.first_author || '',
      publication_year: data?.publication_year ?? undefined,
      doi: data?.doi || '',
      pmid: data?.pmid || '',
      journal: data?.journal || '',
      script_file_base: data?.script_file_base || data?.model_id || '',
      cpp_upload_list: [],
      r_upload_list: [],
    });
    if (formRef) formRef.current = form;
  }, [data, form, formRef]);

  const handleValuesChange = (changedValues: any, allValues: any) => {
    let nextValues = { ...allValues };

    if (changedValues.model_id && !allValues.script_file_base) {
      nextValues = fillEmptyField(form, nextValues, 'script_file_base', changedValues.model_id);
    }

    if (changedValues.script_file_base && !allValues.model_id) {
      nextValues = fillEmptyField(form, nextValues, 'model_id', changedValues.script_file_base);
    }

    const inferredBase = inferScriptBaseFromUploads(nextValues.cpp_upload_list, nextValues.r_upload_list);
    if (inferredBase) {
      nextValues = fillEmptyField(form, nextValues, 'script_file_base', inferredBase);
      nextValues = fillEmptyField(form, nextValues, 'model_id', inferredBase);
    }

    onChange({
      ...data,
      ...nextValues,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
      initialValues={data}
    >
      <Form.Item
        label="模型 ID"
        name="model_id"
        rules={[{ required: true, message: '请输入模型 ID' }]}
        tooltip="唯一标识符，例如 anderson_2006_vancomycin_preterm"
      >
        <Input
          placeholder="例如: anderson_2006_vancomycin_preterm"
          disabled={Boolean(modelId)}
        />
      </Form.Item>

      <Form.Item
        label="展示名称"
        name="display_name"
        rules={[
          { required: true, message: '请输入展示名称' },
          { max: 180, message: '展示名称不能超过 180 个字符' },
        ]}
        tooltip="模型 ID 是永久技术标识；展示名称可随时修改，并用于模型列表和报告。"
      >
        <Input placeholder="例如: colin_2019_vancomycin" />
      </Form.Item>

      <Tabs
        activeKey={importMode}
        onChange={setImportMode}
        items={[
          {
            key: 'candidate',
            label: '模型包 ZIP',
            children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Alert
                  type={packageGuidance.type}
                  showIcon
                  message={packageGuidance.title}
                  description={packageGuidance.detail}
                />
                <input
                  ref={candidateInputRef}
                  type="file"
                  accept=".zip,application/zip"
                  style={{ display: 'none' }}
                  onChange={(event) => void handleCandidateFile(event)}
                />
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  aria-label="选择模型包 ZIP"
                  loading={candidateUploading}
                  disabled={!canUploadCandidate}
                  onClick={() => candidateInputRef.current?.click()}
                >
                  选择模型包 ZIP
                </Button>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="模型包 ID">
                    {candidatePackage?.candidate_id || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="包版本">
                    {candidatePackage?.package_version || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="资格状态">
                    <Tag color={['platform_qualified', 'clinical_validated'].includes(qualificationStatus) ? 'green' : 'blue'}>
                      {qualificationStatus}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="审核状态">
                    <Tag color={reviewStatus === 'approved' ? 'green' : 'orange'}>{reviewStatus}</Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            ),
          },
          {
            key: 'legacy',
            label: 'Legacy 脚本导入',
            children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Alert
                  type={legacyLocked ? 'warning' : 'info'}
                  showIcon
                  message={legacyLocked ? 'Legacy 脚本只读' : 'Legacy 脚本仅用于迁移'}
                  description={legacyLocked
                    ? '当前版本由模型包管理。'
                    : '发布前仍需上传模型包并通过技术资格。'}
                />
                <Form.Item
                  label="脚本名称"
                  name="script_file_base"
                  tooltip="脚本基名会作为 .R / .cpp 文件的统一映射键"
                >
                  <Input
                    placeholder="例如: anderson_2006_vancomycin_preterm"
                    disabled={legacyLocked}
                  />
                </Form.Item>
                <Alert
                  type={scriptStatus.type}
                  showIcon
                  message={`脚本状态：${scriptStatus.label}`}
                  description={(
                    <Space size={[8, 8]} wrap>
                      <Tag color={scriptInfo?.has_cpp_file ? 'green' : 'default'}>
                        {scriptInfo?.has_cpp_file ? `CPP: ${scriptInfo.cpp_filename}` : 'CPP 未上传'}
                      </Tag>
                      <Tag color={scriptInfo?.has_r_file ? 'green' : 'default'}>
                        {scriptInfo?.has_r_file ? `R: ${scriptInfo.r_filename}` : 'R 未上传'}
                      </Tag>
                      <Tag color="blue">
                        映射键: {scriptInfo?.file_base || form.getFieldValue('script_file_base') || data?.model_id || '-'}
                      </Tag>
                      {(scriptInfo?.issues || []).map((issue: string) => (
                        <Tag color="orange" key={issue}>{issue}</Tag>
                      ))}
                    </Space>
                  )}
                />
                <Button onClick={() => void openScriptEditor()} disabled={!resolvedModelId}>
                  {legacyLocked ? '在线查看脚本' : '在线查看/编辑脚本'}
                </Button>
                <Form.Item
                  label="上传 .cpp 脚本"
                  name="cpp_upload_list"
                  getValueFromEvent={normalizeUploadEvent}
                >
                  <ScriptFilePicker
                    accept=".cpp"
                    buttonText={'\u9009\u62e9 .cpp \u6587\u4ef6'}
                    disabled={legacyLocked}
                  />
                </Form.Item>
                <Form.Item
                  label="上传 .R 脚本"
                  name="r_upload_list"
                  getValueFromEvent={normalizeUploadEvent}
                >
                  <ScriptFilePicker
                    accept=".R,.r"
                    buttonText={'\u9009\u62e9 .R \u6587\u4ef6'}
                    disabled={legacyLocked}
                  />
                </Form.Item>
              </Space>
            ),
          },
        ]}
      />

      <Form.Item
        label="第一作者"
        name="first_author"
        rules={[{ required: true, message: '请输入第一作者' }]}
      >
        <Input placeholder="例如: Anderson" />
      </Form.Item>

      <Form.Item
        label="发表年份"
        name="publication_year"
        rules={[{ required: true, message: '请输入发表年份' }]}
      >
        <InputNumber min={1900} max={2100} style={{ width: '100%' }} placeholder="例如: 2006" />
      </Form.Item>

      <Form.Item label="DOI" name="doi">
        <Input placeholder="例如: 10.1124/jpet.106.105940" />
      </Form.Item>

      <Form.Item label="PMID" name="pmid">
        <Input placeholder="例如: 17456721" />
      </Form.Item>

      <Form.Item label="期刊" name="journal">
        <Input placeholder="例如: Journal of Pharmacology and Experimental Therapeutics" />
      </Form.Item>

      <Modal
        title={`脚本编辑器${scriptEditor.file_base ? ` · ${scriptEditor.file_base}` : ''}`}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={() => void handleSaveScriptEditor()}
        confirmLoading={savingEditor}
        okText={legacyLocked ? '脚本只读' : '保存脚本'}
        okButtonProps={{ disabled: legacyLocked }}
        cancelText="关闭"
        width={960}
        destroyOnHidden
      >
        {editorLoading ? (
          <Alert type="info" showIcon message="脚本内容加载中..." />
        ) : (
          <Tabs items={editorTabs} style={{ marginTop: 8 }} />
        )}
      </Modal>
    </Form>
  );
};

export default Step1BasicInfo;
